// ──────────────────────────────────────────────
// 🔹 Devuelve el numero de semana de una fecha
// ──────────────────────────────────────────────
export const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

  // Ajustar al jueves de la semana actual (ISO 8601)
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);

  // Año de la semana ISO
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);

  return {
    year: d.getUTCFullYear(),
    week: weekNo
  };
}

// ──────────────────────────────────────────────
// 🔹 Devuelve el rango (inicio y fin) de una semana ISO 8601
// ──────────────────────────────────────────────
// Calcula el rango de fechas de una semana ISO
export const getWeekDateRange = (weekNumber, year) => {
  const fourthOfJanuary = new Date(year, 0, 4);
  const firstMonday = new Date(fourthOfJanuary);
  firstMonday.setDate(fourthOfJanuary.getDate() - (fourthOfJanuary.getDay() + 6) % 7);

  const startOfWeek = new Date(firstMonday);
  startOfWeek.setDate(firstMonday.getDate() + (weekNumber - 1) * 7);

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  return { start: startOfWeek, end: endOfWeek };
};

// ──────────────────────────────────────────────
// 🔹 Formatea fecha en "DD/MM" o "DD de Mon"
// ──────────────────────────────────────────────
export const formatDate = (date, mode = "short") => {
  if (!(date instanceof Date) || isNaN(date)) return "";

  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleString("es-MX", { month: "short" }).replace(".", "");

  return mode === "short" ? `${day}/${date.getMonth() + 1}` : `${day} de ${month}`;
};

// ──────────────────────────────────────────────
// 🔹 Combina semanas consecutivas (ej. [40-2025, 41-2025])
//     → "29/09 - 12/10"
// ──────────────────────────────────────────────
export const combineConsecutiveWeeks = (weeks) => {
  if (!Array.isArray(weeks) || weeks.length === 0) return "";

  let combined = [];
  let currentRange = null;

  weeks.forEach((week) => {
    const [weekNumber, year] = week.split("-");
    const { start, end } = getWeekDateRange(Number(weekNumber), Number(year));
    if (!start || !end) return;

    if (!currentRange) {
      currentRange = { start, end };
    } else {
      const prevEnd = currentRange.end;
      const isConsecutive = start.getTime() === prevEnd.getTime() + 86400000;

      if (isConsecutive) {
        currentRange.end = end;
      } else {
        combined.push(`${formatDate(currentRange.start)} - ${formatDate(currentRange.end)}`);
        currentRange = { start, end };
      }
    }
  });

  if (currentRange) {
    combined.push(`${formatDate(currentRange.start)} - ${formatDate(currentRange.end)}`);
  }

  return combined.join(", ");
};

// ──────────────────────────────────────────────
// 🔹 Genera etiquetas legibles tipo “13 al 19 de Oct”
// ──────────────────────────────────────────────
export const getWeekLabel = (weekNumber, year, monthStyle = 'short') => {
  const { start, end } = getWeekDateRange(weekNumber, year);
  if (!start || !end) return "";
  const month = end.toLocaleString("es-MX", { month: monthStyle }).replace(".", "");
  return `${start.getDate()} al ${end.getDate()} de ${month}`;
};
//──────────────────────────────────────────────
//🔹  Devuelve una fecha en formato corto “dd al dd”
//──────────────────────────────────────────────
export const formatDateRangeLabel = (start, end) => {
  const monthNames = [
    "ene", "feb", "mar", "abr", "may", "jun",
    "jul", "ago", "sep", "oct", "nov", "dic"
  ];

  const startDay = start.getDate().toString().padStart(2, "0");
  const endDay = end.getDate().toString().padStart(2, "0");
  const monthStart = monthNames[start.getMonth()];
  const monthEnd = monthNames[end.getMonth()];

  return `${startDay + ' ' + monthStart} al ${endDay + ' ' + monthEnd}`;
};
// ──────────────────────────────────────────────
// 🔹 Devuelve las últimas N semanas desde la anterior
// ──────────────────────────────────────────────
export const getLastWeeks = (count = 4) => {
  const today = new Date();
  const year = today.getFullYear();

  // Semana actual ISO
  const jan4 = new Date(year, 0, 4);
  const firstMonday = new Date(jan4);
  firstMonday.setDate(jan4.getDate() - (jan4.getDay() + 6) % 7);
  const diffInDays = Math.floor((today - firstMonday) / (7 * 24 * 60 * 60 * 1000));
  const currentWeek = diffInDays;

  // Obtener las últimas N semanas en orden descendente (más reciente primero)
  const weeks = [];
  for (let i = 0; i < count; i++) {
    const weekNum = currentWeek - i;
    const { start, end } = getWeekDateRange(weekNum, year);
    weeks.push({
      number: weekNum,
      label: formatDateRangeLabel(start, end),
      start,
      end,
    });
  }


  return weeks.sort((a, b) => b.number - a.number);

};

// Devuelve el lunes de la semana (semana = lunes-domingo)
export function getStartDateOfWeek(week, year) {
  const first = new Date(year, 0, 1);
  const day = first.getDay() || 7; // domingo = 7
  const firstMonday = new Date(first);
  firstMonday.setDate(first.getDate() - day + 1); // primer lunes del año

  const monday = new Date(firstMonday);
  monday.setDate(firstMonday.getDate() + (week - 1) * 7);
  monday.setHours(0, 0, 0, 0);

  return monday;
}

// Obtiene la fecha concreta del día de clase dentro de la semana
export function getClassDateOfWeek(week, year, classDaysStr) {
  // classDaysStr puede ser "Lunes,Martes" o array ya parseado; acepta null -> lunes
  const daysOrder = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

  let classDaysArr = [];
  if (!classDaysStr) {
    classDaysArr = ["Lunes"];
  } else if (Array.isArray(classDaysStr)) {
    classDaysArr = classDaysStr;
  } else if (typeof classDaysStr === "string") {
    classDaysArr = classDaysStr.split(",").map(d => d.trim()).filter(Boolean);
  }

  // Tomamos el primer día de la semana (el más temprano) si hay varios
  const classDayIndex = classDaysArr.length > 0
    ? Math.min(...classDaysArr.map(day => {
      const idx = daysOrder.indexOf(day);
      return idx === -1 ? 0 : idx;
    }))
    : 0; // 0 = Lunes

  const monday = getStartDateOfWeek(week, year);
  const classDate = new Date(monday);
  classDate.setDate(monday.getDate() + classDayIndex); // +0 lunes, +5 sabado, etc.
  classDate.setHours(0, 0, 0, 0);
  return classDate;
}

// Me devuelve fecha exacta del lunes de la semana colocada y la fecha final de dicha semana
export const getWeekRange = (weekNumber, year) => {
        // Obtener el lunes de la primera semana (la que contiene el 1 de enero)
        const jan4 = new Date(year, 0, 4); // el 4 de enero siempre cae en la semana 1 ISO
        const dayOfWeek = jan4.getDay(); // 0 = domingo, 1 = lunes, ..., 6 = sábado
        const diffToMonday = (dayOfWeek + 6) % 7; // cuántos días restar para llegar a lunes

        const firstMonday = new Date(jan4);
        firstMonday.setDate(jan4.getDate() - diffToMonday);

        // Obtener el lunes de la semana deseada
        const startDate = new Date(firstMonday);
        startDate.setDate(startDate.getDate() + (weekNumber - 1) * 7);

        // Fin de semana (domingo)
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);

        // Formatear fechas a dd/mm/yyyy
        const format = (date) => {
            const dd = String(date.getDate()).padStart(2, '0');
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const yyyy = date.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
        };

        return `${format(startDate)} - ${format(endDate)}`;
    }

    // ──────────────────────────────────────────────
// 🔹 Devuelve las últimas N semanas atras y adelante
// ──────────────────────────────────────────────
export const getWeeksRange = ({ back = 4, forward = 1 } = {}) => {
  const today = new Date();
  const { week: currentWeek, year: currentYear } = getWeekNumber(today);

  const weeks = [];

  // 🔙 HACIA ATRÁS
  for (let i = back; i > 0; i--) {
    const date = new Date(today);
    date.setDate(today.getDate() - i * 7);

    const { week, year } = getWeekNumber(date);
    const { start, end } = getWeekDateRange(week, year);

    weeks.push({
      number: week,
      year,
      label: formatDateRangeLabel(start, end),
      start,
      end,
    });
  }

  // 📍 ACTUAL
  const currentRange = getWeekDateRange(currentWeek, currentYear);

  weeks.push({
    number: currentWeek,
    year: currentYear,
    label: formatDateRangeLabel(currentRange.start, currentRange.end),
    start: currentRange.start,
    end: currentRange.end,
  });

  // 🔜 HACIA ADELANTE
  for (let i = 1; i <= forward; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i * 7);

    const { week, year } = getWeekNumber(date);
    const { start, end } = getWeekDateRange(week, year);

    weeks.push({
      number: week,
      year,
      label: formatDateRangeLabel(start, end),
      start,
      end,
    });
  }

  // 🧠 ORDEN SEGURO (anti año nuevo 🎆)
  return weeks.sort(
    (a, b) => (b.year * 100 + b.number) - (a.year * 100 + a.number)
  );
};